import { mutatePurchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { purchaseOrderItemStatusColor, setPurchaseOrderItemStatus } from "@/utils/scmDataStatus";
import { setToLocaleString } from "@/utils/unitMark";
import styled from "@emotion/styled";
import { ActionIcon, Badge, Checkbox, Flex, useMantineColorScheme } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconSearch, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useModal } from "../modal/ModalStackManager";
import { PurchaseOrderItemsUpdateForm } from "../purchaseOrderItems/form/UpdateForm";

import { users } from "@/api/users/useUsersQuery";
import timeUtil from "@/utils/timeUtil";
import { OrderItemsUpdateForm } from "../orderItems/form/updarteForm";
import { PurchaseOrderItemsDetailForm } from "../purchaseOrderItems/form/PurchaseOrderItemDetailForm";
import customAlert from "../ui/alert/Alert";
import { ConfirmForm } from "../ui/confirm";
import { itemTypeColor } from "@/constants/items";

interface PurchaseOrderTableInRowBodyProps {
  item: AuthSignupPost201ResponseEquipmentPurchaseOrderItem;
  idex: number;
  checkedItemIds: Set<number | undefined>;
  handleToggleItem: (id: number) => void;
  pageKey: "purchaseOrderItems" | "orderItems";
}
export const PurchaseOrderTableInRowBody = (params: PurchaseOrderTableInRowBodyProps) => {
  const { item, idex, checkedItemIds, handleToggleItem, pageKey } = params;
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();
  const { data: userInfo } = useQuery(users.who({}));
  const partnerType = userInfo?.data?.partner?.type;
  const { colorScheme } = useMantineColorScheme();

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest) =>
      mutatePurchaseOrderItems
        .delete(params)
        .mutationFn(params as ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest | any),
    {
      onSuccess: () => {
        // refetch();
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
          }),
          `${t("common.deleteSuccess")}`,
          "green"
        );
        closeModal(null);
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
          }),
          t("common.deleteFail", {
            item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
          }),
          "red"
        );
      },
    }
  );

  const deletePurchaseOrderItemAction = () => {
    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          deleteMutate({ purchaseOrderItemId: item?.id as number });
        }}
        noCallback={() => {
          closeModal(null);
        }}
      />,
      null,
      t("common.delete", {
        item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
      })
    );
  };

  return (
    <tr key={idex}>
      <Td theme={{ colorScheme }}>
        <Flex justify="center">
          <Checkbox
            checked={checkedItemIds.has(item.id)}
            onChange={() => handleToggleItem(item.id ?? 0)}
          />
        </Flex>
      </Td>
      <Td theme={{ colorScheme }}>
        <Badge
          variant="filled"
          color={
            purchaseOrderItemStatusColor[item.status as keyof typeof purchaseOrderItemStatusColor]
          }
        >
          {setPurchaseOrderItemStatus(item.status ?? "")}
        </Badge>
      </Td>
      <Td theme={{ colorScheme }}>
        <Badge size="lg" color={itemTypeColor[item?.itemType as keyof typeof itemTypeColor]}>
          {t(item?.itemType as string)}
        </Badge>
      </Td>
      <Td theme={{ colorScheme }}>{item.itemName}</Td>
      <Td theme={{ colorScheme }}>{item?.gradeName}</Td>
      <Td theme={{ colorScheme }}>{item?.itemCategoryName}</Td>
      <Td theme={{ colorScheme }}>{item?.abstractItemCode}</Td>
      <Td theme={{ colorScheme }}>
        {pageKey === "purchaseOrderItems" ? item.sourceLocationName : item.targetLocationName}
      </Td>
      <Td theme={{ colorScheme }} right={true}>
        {setToLocaleString(item.quantity)}
      </Td>
      <Td theme={{ colorScheme }} right={true}>
        {setToLocaleString(item.sumOfProductionPlanQuantity)}
      </Td>
      <Td theme={{ colorScheme }} right={true}>
        {setToLocaleString(item.sumOfStockPlanQuantity)}
      </Td>
      <Td theme={{ colorScheme }} right={true}>
        {setToLocaleString(item.sumOfDeliveredQuantity)}
      </Td>
      <Td theme={{ colorScheme }}>{timeUtil(item.orderDate ?? "")}</Td>
      <Td theme={{ colorScheme }}>{timeUtil(item.deliveryDeadline ?? "")}</Td>
      <Td theme={{ colorScheme }}>
        <Flex direction={"row"} gap="md" justify="center">
          <ActionIcon
            color="blue"
            variant="subtle"
            onClick={() =>
              openModal(
                <PurchaseOrderItemsDetailForm purchaseOrderItemId={item?.id!!} pageKey={pageKey} />,
                null,
                t("common.detail", {
                  item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
                })
              )
            }
          >
            <IconSearch />
          </ActionIcon>
          <ActionIcon>
            <IconEdit
              color="orange"
              onClick={() =>
                openModal(
                  pageKey === "purchaseOrderItems" ? (
                    <PurchaseOrderItemsUpdateForm formatterProps={item} />
                  ) : (
                    <OrderItemsUpdateForm formatterProps={item} />
                  ),
                  null,
                  t("common.edit", {
                    item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
                  })
                )
              }
            />
          </ActionIcon>
          <ActionIcon>
            <IconTrash color="red" onClick={deletePurchaseOrderItemAction} />
          </ActionIcon>
        </Flex>
      </Td>
    </tr>
  );
};

const Td = styled.td<{ right?: boolean; theme: { colorScheme: "dark" | "light" } }>`
  padding: 0 10px;
  text-align: ${(props) => (props.right ? "right" : "center")};
  vertical-align: middle;
  background-color: ${({ theme }) =>
    theme.colorScheme === "dark" ? "var(--gray-9, #111)" : "var(--gray-0, #fff)"};
`;
